import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { apiService } from "../../services/apiService";
import { API_ENDPOINTS } from "../../services/apiEndpoints";

const SanitarywareAttributes = () => {
  const [attributes, setAttributes] = useState([]);
  const [filteredAttributes, setFilteredAttributes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [typeCount, setTypeCount] = useState(0);
  const [modalMode, setModalMode] = useState("add");
  const [editingAttribute, setEditingAttribute] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deletingAttribute, setDeletingAttribute] = useState(null);
  const [undoDeprecationModal, setUndoDeprecationModal] = useState(false);
  const [undoDeprecatingAttribute, setUndoDeprecatingAttribute] =
    useState(null);
  const [previewModal, setPreviewModal] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterRequired, setFilterRequired] = useState("all");
  const [filterDeprecated, setFilterDeprecated] = useState("all");
  const [formData, setFormData] = useState({
    name: "",
    display_name: "",
    is_required: false,
    values: [{ value: "", display_name: "" }],
  });

  const fetchAttributes = async () => {
    setLoading(true);
    try {
      const response = await apiService.get(
        API_ENDPOINTS.SANITARYWARE.ATTRIBUTES_LIST
      );
      if (response.attributes) {
        setAttributes(response.attributes || []);
        setTypeCount(response.count || 0);
      } else {
        toast.error(response.message || "Failed to fetch attributes");
      }
    } catch (error) {
      console.error("Error fetching attributes:", error);
      toast.error("Failed to fetch attributes");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleValueChange = (index, field, value) => {
    const newValues = [...formData.values];
    newValues[index] = {
      ...newValues[index],
      [field]: value,
    };
    setFormData({
      ...formData,
      values: newValues,
    });
  };

  const addValueField = () => {
    setFormData({
      ...formData,
      values: [...formData.values, { value: "", display_name: "" }],
    });
  };

  const removeValueField = (index) => {
    if (formData.values.length > 1) {
      const newValues = formData.values.filter((_, i) => i !== index);
      setFormData({
        ...formData,
        values: newValues,
      });
    }
  };

  const openAddModal = () => {
    setModalMode("add");
    setFormData({
      name: "",
      display_name: "",
      is_required: false,
      values: [{ value: "", display_name: "" }],
    });
    setEditingAttribute(null);
    setShowModal(true);
  };

  const openEditModal = async (attribute) => {
    try {
      const response = await apiService.get(
        API_ENDPOINTS.SANITARYWARE.ATTRIBUTES_DETAIL(attribute.id)
      );
      if (response.attribute) {
        const values = response.values.map((value) => ({
          value: value.value || "",
          display_name: value.display_name || "",
        }));
        setFormData({
          name: response.attribute.name || "",
          display_name: response.attribute.display_name || "",
          is_required: response.attribute.is_required || false,
          values: values || [{ value: "", display_name: "" }],
        });
        setEditingAttribute(response.attribute);
        setModalMode("edit");
        setShowModal(true);
      } else {
        toast.error(response.message || "Failed to fetch attribute details");
      }
    } catch (error) {
      console.error("Error fetching attribute details:", error);
      toast.error("Failed to fetch attribute details");
    }
  };

  const openDeleteModal = (attribute) => {
    setDeletingAttribute(attribute);
    setDeleteModal(true);
  };

  const openUndoDeprecationModal = (attribute) => {
    setUndoDeprecatingAttribute(attribute);
    setUndoDeprecationModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.display_name.trim()) {
      toast.error("Name and Display Name are required");
      return;
    }
    if (!/^[a-z][a-z0-9_]*$/.test(formData.name.trim())) {
      toast.error("Name should be in snake_case format (e.g., flush_type)");
      return;
    }
    const validValues = formData.values.filter((v) => v.value.trim());
    if (validValues.length === 0) {
      toast.error("At least one value is required");
      return;
    }
    const cleanedValues = validValues.map((v) => ({
      value: v.value.trim(),
      ...(v.display_name.trim() && { display_name: v.display_name.trim() }),
    }));
    const submitData = {
      ...formData,
      name: formData.name.trim(),
      display_name: formData.display_name.trim(),
      values: cleanedValues,
    };
    try {
      if (modalMode === "add") {
        const response = await apiService.post(
          API_ENDPOINTS.SANITARYWARE.ATTRIBUTES_CREATE,
          submitData
        );
        if (response.id) {
          toast.success(response.message || "Attribute created successfully");
          setShowModal(false);
          fetchAttributes();
        } else {
          toast.error(response.message || "Failed to create attribute");
        }
      } else {
        const response = await apiService.post(
          API_ENDPOINTS.SANITARYWARE.ATTRIBUTES_UPDATE(editingAttribute.id),
          submitData
        );
        if (response) {
          toast.success(response.message || "Attribute updated successfully");
          setShowModal(false);
          fetchAttributes();
        } else {
          toast.error(response.message || "Failed to update attribute");
        }
      }
    } catch (error) {
      console.error("Error saving attribute:", error);
      toast.error("Failed to save attribute");
    }
  };

  const handleDelete = async () => {
    if (!deletingAttribute) return;
    try {
      const response = await apiService.delete(
        API_ENDPOINTS.SANITARYWARE.ATTRIBUTES_DELETE(deletingAttribute.id)
      );
      if (response) {
        toast.success(response.message || "Attribute deleted successfully");
        setDeleteModal(false);
        setDeletingAttribute(null);
        fetchAttributes();
      } else {
        toast.error(response.message || "Failed to delete attribute");
      }
    } catch (error) {
      console.error("Error deleting attribute:", error);
      toast.error("Failed to delete attribute");
    }
  };

  const handleUndoDeprecation = async () => {
    if (!undoDeprecatingAttribute) return;
    try {
      const response = await apiService.post(
        API_ENDPOINTS.SANITARYWARE.ATTRIBUTES_UNDO_DEPRECATION(
          undoDeprecatingAttribute.id
        )
      );
      if (response) {
        toast.success(response.message || "Deprecation undone successfully");
        setUndoDeprecationModal(false);
        setUndoDeprecatingAttribute(null);
        fetchAttributes();
      } else {
        toast.error(response.message || "Failed to undo deprecation");
      }
    } catch (error) {
      console.error("Error undoing deprecation:", error);
      toast.error("Failed to undo deprecation");
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setFormData({
      name: "",
      display_name: "",
      is_required: false,
      values: [{ value: "", display_name: "" }],
    });
    setEditingAttribute(null);
  };

  const closeDeleteModal = () => {
    setDeleteModal(false);
    setDeletingAttribute(null);
  };

  const closeUndoDeprecationModal = () => {
    setUndoDeprecationModal(false);
    setUndoDeprecatingAttribute(null);
  };

  const closePreviewModal = () => {
    setPreviewModal(false);
    setPreviewData(null);
  };

  useEffect(() => {
    fetchAttributes();
  }, []);

  useEffect(() => {
    let filtered = [...attributes];
    if (searchTerm.trim()) {
      filtered = filtered.filter(
        (attr) =>
          attr.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          attr.display_name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    if (filterRequired !== "all") {
      filtered = filtered.filter((attr) =>
        filterRequired === "required" ? attr.is_required : !attr.is_required
      );
    }
    if (filterDeprecated !== "all") {
      filtered = filtered.filter((attr) =>
        filterDeprecated === "deprecated"
          ? attr.is_deprecated
          : !attr.is_deprecated
      );
    }
    setFilteredAttributes(filtered);
  }, [attributes, searchTerm, filterRequired, filterDeprecated]);

  return (
    <>
      <div className="page-header mb-3">
        <div className="row align-items-center">
          <div className="col">
            <h3 className="page-title">Sanitaryware Attributes</h3>
          </div>
          <div className="col-auto text-end float-end ms-auto">
            <button className="btn btn-primary" onClick={openAddModal}>
              <i className="fe fe-plus"></i> Add New Attribute
            </button>
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-sm-12">
          <div className="card">
            <div className="card-header">
              <h5 className="card-title">Attributes</h5>
              <span className="badge bg-primary">Total: {typeCount}</span>
            </div>
            <div className="card-body">
              {loading ? (
                <div className="text-center py-4">
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-striped table-hover">
                    <thead>
                      <tr>
                        <th>Sr.No</th>
                        <th>Name</th>
                        <th>Display Name</th>
                        <th>Required</th>
                        <th>Values</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredAttributes.length === 0 ? (
                        <tr>
                          <td colSpan="7" className="text-center">
                            {attributes.length === 0
                              ? "No attributes found"
                              : "No attributes match the current filters"}
                          </td>
                        </tr>
                      ) : (
                        filteredAttributes.map((attribute,index) => (
                          <tr key={index}>
                            <td>{index + 1}</td>
                            <td>
                              <code className="text-primary">
                                {attribute.name}
                              </code>
                            </td>
                            <td>{attribute.display_name}</td>
                            <td>
                              <span
                                className={`badge ${
                                  attribute.is_required
                                    ? "bg-success"
                                    : "bg-secondary"
                                }`}
                              >
                                {attribute.is_required
                                  ? "Required"
                                  : "Optional"}
                              </span>
                            </td>
                            <td>
                              <div className="d-flex flex-wrap gap-1">
                                {attribute.values
                                  ?.slice(0, 3)
                                  .map((value, idx) => (
                                    <span
                                      key={idx}
                                      className="badge bg-light text-dark"
                                    >
                                      {value.display_name || value.value}
                                    </span>
                                  ))}
                                {attribute.values?.length > 3 && (
                                  <span className="badge bg-info">
                                    +{attribute.values.length - 3} more
                                  </span>
                                )}
                              </div>
                            </td>
                            <td>
                              <span
                                className={`badge ${
                                  attribute.is_deprecated
                                    ? "bg-warning"
                                    : "bg-success"
                                }`}
                              >
                                {attribute.is_deprecated
                                  ? "Deprecated"
                                  : "Active"}
                              </span>
                            </td>
                            <td>
                              <div className="actions">
                                <button
                                  className="btn btn-sm btn-outline-primary me-2"
                                  onClick={() => openEditModal(attribute)}
                                  title="Edit"
                                >
                                  <i className="fe fe-edit"></i>
                                </button>
                                {attribute.is_deprecated && (
                                  <button
                                    className="btn btn-sm btn-outline-warning me-2"
                                    onClick={() =>
                                      openUndoDeprecationModal(attribute)
                                    }
                                    title="Undo Deprecation"
                                  >
                                    <i className="fe fe-rotate-ccw"></i>
                                  </button>
                                )}
                                <button
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => openDeleteModal(attribute)}
                                  title="Delete"
                                >
                                  <i className="fe fe-trash-2"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {showModal && (
        <div
          className="modal fade show"
          style={{ display: "block" }}
          tabIndex="-1"
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {modalMode === "add" ? "Add New Attribute" : "Edit Attribute"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeModal}
                ></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label htmlFor="name" className="form-label">
                          Name <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          placeholder="e.g., flush_type"
                          required
                        />
                        <small className="form-text text-muted">
                          Internal identifier (snake_case)
                        </small>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label htmlFor="display_name" className="form-label">
                          Display Name <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="display_name"
                          name="display_name"
                          value={formData.display_name}
                          onChange={handleInputChange}
                          placeholder="e.g., Flush Type"
                          required
                        />
                        <small className="form-text text-muted">
                          User-friendly display name
                        </small>
                      </div>
                    </div>
                  </div>
                  <div className="mb-3">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="is_required"
                        name="is_required"
                        checked={formData.is_required}
                        onChange={handleInputChange}
                      />
                      <label className="form-check-label" htmlFor="is_required">
                        Required Field
                      </label>
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">
                      Values <span className="text-danger">*</span>
                    </label>
                    <div className="row mb-2">
                      <div className="col-md-5">
                        <small className="text-muted">Value (Internal)</small>
                      </div>
                      <div className="col-md-5">
                        <small className="text-muted">
                          Display Name (Optional)
                        </small>
                      </div>
                      <div className="col-md-2"></div>
                    </div>
                    {formData.values.map((value, index) => (
                      <div key={index} className="row mb-2">
                        <div className="col-md-5">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="e.g., Single"
                            value={value.value}
                            onChange={(e) =>
                              handleValueChange(index, "value", e.target.value)
                            }
                            required
                          />
                        </div>
                        <div className="col-md-5">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="e.g., Single Flush (optional)"
                            value={value.display_name}
                            onChange={(e) =>
                              handleValueChange(
                                index,
                                "display_name",
                                e.target.value
                              )
                            }
                          />
                        </div>
                        <div className="col-md-2">
                          {formData.values.length > 1 && (
                            <button
                              type="button"
                              className="btn btn-outline-danger btn-sm"
                              onClick={() => removeValueField(index)}
                              title="Remove Value"
                            >
                              <i className="fe fe-trash-2"></i>
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      onClick={addValueField}
                    >
                      <i className="fe fe-plus"></i> Add Value
                    </button>
                    <small className="form-text text-muted d-block mt-2">
                      At least one value is required. Display names are optional
                      but recommended for better user experience.
                    </small>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary me-2"
                    onClick={closeModal}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    {modalMode === "add" ? "Create" : "Update"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      {previewModal && previewData && (
        <div
          className="modal fade show"
          style={{ display: "block" }}
          tabIndex="-1"
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Preview Update</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closePreviewModal}
                ></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-6">
                    <h6>Current Values</h6>
                    <ul className="list-group">
                      {previewData.current?.values?.map((value, index) => (
                        <li key={index} className="list-group-item">
                          <strong>{value.value}</strong>
                          {value.display_name && ` - ${value.display_name}`}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="col-md-6">
                    <h6>New Values</h6>
                    <ul className="list-group">
                      {previewData.new?.values?.map((value, index) => (
                        <li key={index} className="list-group-item">
                          <strong>{value.value}</strong>
                          {value.display_name && ` - ${value.display_name}`}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                {previewData.changes && (
                  <div className="mt-3">
                    <h6>Changes Summary</h6>
                    <div className="alert alert-info">
                      {previewData.changes}
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closePreviewModal}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {deleteModal && (
        <div
          className="modal fade show"
          style={{ display: "block" }}
          tabIndex="-1"
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Delete</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeDeleteModal}
                ></button>
              </div>
              <div className="modal-body">
                <p>
                  Are you sure you want to delete the attribute "
                  <strong>
                    {deletingAttribute?.display_name || deletingAttribute?.name}
                  </strong>
                  "?
                </p>
                <p className="text-danger">This action cannot be undone.</p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary me-2"
                  onClick={closeDeleteModal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleDelete}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {undoDeprecationModal && (
        <div
          className="modal fade show"
          style={{ display: "block" }}
          tabIndex="-1"
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Undo Deprecation</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeUndoDeprecationModal}
                ></button>
              </div>
              <div className="modal-body">
                <p>
                  Are you sure you want to undo the deprecation for "
                  <strong>
                    {undoDeprecatingAttribute?.display_name ||
                      undoDeprecatingAttribute?.name}
                  </strong>
                  "?
                </p>
                <p className="text-warning">
                  This will make the attribute active again and available for
                  use.
                </p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary me-2"
                  onClick={closeUndoDeprecationModal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-warning"
                  onClick={handleUndoDeprecation}
                >
                  Undo Deprecation
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {(showModal || deleteModal || undoDeprecationModal || previewModal) && (
        <div className="modal-backdrop fade show"></div>
      )}
    </>
  );
};

export default SanitarywareAttributes;
